import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import faqData from '../../data/faqData';

export const FAQSection = () => {
  const { t, language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(0);
  const [openQuestion, setOpenQuestion] = useState(null);

  const categories = faqData[language];

  return (
    <section id="faq" className="py-24 bg-[#0B1521]" data-testid="faq-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#4A90D9]/20 text-[#4A90D9] text-sm font-medium tracking-wide mb-4">
            {t('faq_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            {t('faq_title')}
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {t('faq_subtitle')}
          </p>
        </motion.div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((category, index) => (
            <button
              key={index}
              onClick={() => {
                setActiveCategory(index);
                setOpenQuestion(null);
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                activeCategory === index
                  ? 'bg-[#4A90D9] text-white'
                  : 'bg-[#152840] text-white/70 hover:text-white'
              }`}
              data-testid={`faq-category-${index}`}
            >
              {category.category}
            </button>
          ))}
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {categories[activeCategory].questions.map((item, index) => {
            const isOpen = openQuestion === index;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#152840] rounded-xl overflow-hidden border border-white/5"
              >
                <button
                  onClick={() => setOpenQuestion(isOpen ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`faq-question-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle size={20} className="text-[#4A90D9] flex-shrink-0" />
                    <span className="font-medium text-white">{item.q}</span>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`text-white/60 transition-transform duration-200 flex-shrink-0 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-5 pb-5 text-white/70 leading-relaxed border-t border-white/5 pt-4 ml-8">
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
